import { useState, useRef } from "react";
import { useNotification } from "../../../context/notification-provider";
import { deleteTemplateValidCharacters } from "../services/template-service";
import type {
  ValidCharacterItem,
  AvailableValidCharacterItem,
} from "../types/api-types";
import type { FormData } from "../template-configuration/template-advanced-config";

interface UseValidCharactersSelectionProps {
  validCharactersList: ValidCharacterItem[];
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  onSaveCharacters: (
    added: ValidCharacterItem[],
    deleted: ValidCharacterItem[],
  ) => Promise<void>;
}

const useValidCharactersSelection = ({
  validCharactersList,
  setFormData,
  onSaveCharacters,
}: UseValidCharactersSelectionProps) => {
  const { showNotification } = useNotification();
  const [openDialog, setOpenDialog] = useState(false);
  const [pendingSelections, setPendingSelections] = useState<
    ValidCharacterItem[]
  >([]);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    character: ValidCharacterItem | null;
  }>({ open: false, character: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const initialSelectionsRef = useRef<ValidCharacterItem[]>([]);

  const handleOpenDialog = () => {
    initialSelectionsRef.current = [...validCharactersList];
    setPendingSelections([...validCharactersList]);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setPendingSelections([]);
  };

  const handleToggleCharacter = (character: AvailableValidCharacterItem) => {
    setPendingSelections((prev) => {
      const isSelected = prev.some((c) => c.asciiValue === character.id);
      if (isSelected) {
        return prev.filter((c) => c.asciiValue !== character.id);
      }
      const newCharacter: ValidCharacterItem = {
        id: 0,
        templateId: 0,
        asciiValue: character.id,
        description: character.value,
      };
      return [...prev, newCharacter];
    });
  };

  const handleSaveSelections = async () => {
    setIsSaving(true);
    try {
      const addedCharacters = pendingSelections.filter(
        (pending) =>
          !initialSelectionsRef.current.some(
            (initial) => initial.asciiValue === pending.asciiValue,
          ),
      );

      const deletedCharacters = initialSelectionsRef.current.filter(
        (initial) =>
          !pendingSelections.some(
            (pending) => pending.asciiValue === initial.asciiValue,
          ),
      );

      await onSaveCharacters(addedCharacters, deletedCharacters);

      setFormData((prev) => ({
        ...prev,
        validCharactersList: pendingSelections,
      }));

      handleCloseDialog();
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveCharacter = (character: ValidCharacterItem) => {
    setConfirmDialog({ open: true, character });
  };

  const handleCancelRemove = () => {
    setConfirmDialog({ open: false, character: null });
  };

  const handleConfirmRemove = async () => {
    if (!confirmDialog.character) return;

    const characterToRemove = confirmDialog.character;
    setIsDeleting(true);
    try {
      await deleteTemplateValidCharacters(characterToRemove);

      setFormData((prev) => ({
        ...prev,
        validCharactersList: prev.validCharactersList.filter(
          (char) => char.id !== characterToRemove.id,
        ),
      }));

      setConfirmDialog({ open: false, character: null });

      showNotification({
        message: "Character removed successfully",
        severity: "success",
      });
    } catch (err) {
      showNotification({
        message: `Failed to remove character. ${(err as Error).message}`,
        severity: "error",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    openDialog,
    pendingSelections,
    isSaving,
    confirmDialog,
    isDeleting,
    handleOpenDialog,
    handleCloseDialog,
    handleToggleCharacter,
    handleSaveSelections,
    handleRemoveCharacter,
    handleConfirmRemove,
    handleCancelRemove,
  };
};

export default useValidCharactersSelection;
